import React from 'react';

/**
 * The people attached to an HR requisition's line items.
 *
 * HR requisitions let a requester name who a line is FOR — a staff member, a
 * non-employee, or an "other employee" — with a rate, hours or days against
 * each. That was captured at creation and then never shown again: the detail
 * page displayed the line and its amount with no sign of who it covered.
 *
 * Presence is the test, not the department. A requisition HAS people attached
 * or it does not; keying off a department name or a request type would miss an
 * HR-style requisition raised from somewhere else, and would show an empty
 * table for an HR one that names nobody.
 */

const fmt = (v) =>
  v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—';

const dash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);

const th = {
  padding: '9px 10px', textAlign: 'center', fontWeight: 700, fontSize: 10.5,
  whiteSpace: 'nowrap', color: '#fff', background: '#0b318bff',
  border: '1px solid #cbd5e1',
};
const td = {
  padding: '7px 10px', textAlign: 'center', fontSize: 12,
  border: '1px solid #cbd5e1',
};

// Three kinds of row, told apart the way the data actually distinguishes them.
// employee_user_id is the only reliable marker of a real staff member;
// non_employee_category separates the other two, and is null on rows created
// before that split existed — those fall back to Non-Employee.
const KIND = {
  employee:       { label: 'Employee',       color: '#15803d', bg: '#dcfce7' },
  other_employee: { label: 'Other Employee', color: '#b45309', bg: '#fef3c7' },
  non_employee:   { label: 'Non-Employee',   color: '#1d4ed8', bg: '#dbeafe' },
};
const kindOf = (p) => {
  if (p.employee_user_id) return KIND.employee;
  if (p.non_employee_category === 'other_employee') return KIND.other_employee;
  return KIND.non_employee;
};

const COLS = [
  ['LINE ITEM', 190], ['TYPE', 120], ['NAME', 170], ['DESIGNATION', 150],
  ['RATE (₦)', 110], ['HOURS', 80], ['DAYS', 80], ['AMOUNT (₦)', 120],
  ['REMARKS', 160],
];

export default function HREmployeesTable({ items = [] }) {
  // Flattened across lines, each row remembering which line it came from —
  // one table reads better than a fragment under every line item.
  const people = [];
  (items || []).forEach((item) => {
    (item.hr_employees || []).forEach((p) => {
      people.push({ ...p, _line: item.description || item.material_name || '—' });
    });
  });

  if (people.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, padding: '0 16px' }}>
        PERSONNEL ON THIS REQUISITION ({people.length})
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>#</th>
              {COLS.map(([label, min]) => (
                <th key={label} style={{ ...th, minWidth: min }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map((p, i) => {
              const kind = kindOf(p);
              return (
                <tr key={p.hr_employee_id || i}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{dash(p._line)}</td>
                  <td style={td}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                      fontSize: 11, fontWeight: 700, color: kind.color, background: kind.bg,
                      whiteSpace: 'nowrap',
                    }}>
                      {kind.label}
                    </span>
                  </td>
                  {/* A staff member carries a resolved name; the other two
                      carry whatever was typed in. */}
                  <td style={{ ...td, fontWeight: 600 }}>
                    {dash(p.employee_name || p.non_employee_full_name)}
                  </td>
                  <td style={td}>{dash(p.non_employee_designation)}</td>
                  <td style={td}>{fmt(p.non_employee_rate)}</td>
                  <td style={td}>{dash(p.non_employee_hours)}</td>
                  <td style={td}>{dash(p.non_employee_days)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt(p.non_employee_amount)}</td>
                  <td style={td}>{dash(p.non_employee_remarks)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
